// ===========================================
// PREMIUM CAMPAIGNS PAGE - VP-Level Design
// Matching Tripnovac Platform Hub Quality
// Marketing Campaign Management
// ===========================================

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { campaignsService, Campaign as ApiCampaign } from '../../services/campaigns.service';
import { aiService } from '../../services/ai.service';
import { toast } from 'react-hot-toast';
import {
  Megaphone,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Mail,
  Calendar,
  Users,
  BarChart3,
  TrendingUp,
  TrendingDown,
  Eye,
  MousePointerClick,
  Send,
  Play,
  Pause,
  Edit2,
  Copy,
  Trash2,
  Clock,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  ChevronDown,
  Sparkles,
  Target,
  Zap,
  Globe,
  MessageSquare,
  Share2,
  Tag,
  RefreshCw,
  ExternalLink,
  X,
  Settings,
  Layers,
} from 'lucide-react';

interface Campaign {
  id: string;
  name: string;
  type: 'email' | 'sms' | 'social' | 'automation';
  status: 'active' | 'paused' | 'draft' | 'completed' | 'scheduled';
  recipients: number;
  sent: number;
  opened: number;
  clicked: number;
  openRate: number;
  clickRate: number;
  createdAt: string;
  scheduledFor?: string;
  tags: string[];
}

// Helper to transform API campaign to display format
const transformCampaign = (c: ApiCampaign): Campaign => ({
  id: c.id,
  name: c.name,
  type: (c.type?.toLowerCase() || 'email') as Campaign['type'],
  status: (c.status?.toLowerCase() || 'draft') as Campaign['status'],
  recipients: c.metrics?.sent || c.sentCount || 0,
  sent: c.sentCount || c.metrics?.sent || 0,
  opened: c.openCount || c.metrics?.opened || 0,
  clicked: c.clickCount || c.metrics?.clicked || 0,
  openRate: c.sentCount > 0 ? Math.round((c.openCount / c.sentCount) * 100 * 10) / 10 : 0,
  clickRate: c.openCount > 0 ? Math.round((c.clickCount / c.openCount) * 100 * 10) / 10 : 0,
  createdAt: c.createdAt,
  scheduledFor: c.scheduledFor,
  tags: [],
});

// Stat Card Component
const StatCard: React.FC<{
  title: string;
  value: string | number;
  change?: number;
  icon: React.ReactNode;
  gradient: string;
  subtitle?: string;
}> = ({ title, value, change, icon, gradient, subtitle }) => (
  <motion.div
    whileHover={{ y: -2 }}
    className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm"
  >
    <div className="flex items-start justify-between">
      <div className={`w-11 h-11 rounded-xl ${gradient} flex items-center justify-center text-white shadow-lg`}>
        {icon}
      </div>
      {change !== undefined && (
        <span className={`flex items-center gap-1 text-xs font-bold ${change >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
          {change >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {change >= 0 ? '+' : ''}{change}%
        </span>
      )}
    </div>
    <h3 className="text-2xl font-bold text-gray-900 mt-3">{value}</h3>
    <p className="text-sm text-gray-500">{title}</p>
    {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
  </motion.div>
);

// Campaign Card Component
const CampaignCard: React.FC<{
  campaign: Campaign;
  onEdit: () => void;
  onPause: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onView: () => void;
}> = ({ campaign, onEdit, onPause, onDuplicate, onDelete, onView }) => {
  const [showMenu, setShowMenu] = useState(false);

  const statusConfig = {
    active: { color: 'bg-emerald-100 text-emerald-700', icon: <Play className="w-3 h-3" />, label: 'Active' },
    paused: { color: 'bg-amber-100 text-amber-700', icon: <Pause className="w-3 h-3" />, label: 'Paused' },
    draft: { color: 'bg-gray-100 text-gray-700', icon: <Edit2 className="w-3 h-3" />, label: 'Draft' },
    completed: { color: 'bg-blue-100 text-blue-700', icon: <CheckCircle2 className="w-3 h-3" />, label: 'Completed' },
    scheduled: { color: 'bg-violet-100 text-violet-700', icon: <Clock className="w-3 h-3" />, label: 'Scheduled' },
  };

  const typeConfig = {
    email: { icon: <Mail className="w-5 h-5" />, gradient: 'bg-gradient-to-br from-blue-500 to-cyan-500' },
    sms: { icon: <MessageSquare className="w-5 h-5" />, gradient: 'bg-gradient-to-br from-emerald-500 to-teal-500' },
    social: { icon: <Share2 className="w-5 h-5" />, gradient: 'bg-gradient-to-br from-violet-500 to-purple-500' },
    automation: { icon: <Zap className="w-5 h-5" />, gradient: 'bg-gradient-to-br from-amber-500 to-orange-500' },
  };

  const status = statusConfig[campaign.status];
  const type = typeConfig[campaign.type];

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm hover:shadow-lg transition-all group"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-xl ${type.gradient} flex items-center justify-center text-white shadow-lg`}>
            {type.icon}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 group-hover:text-violet-600 transition-colors">
              {campaign.name}
            </h3>
            <p className="text-sm text-gray-500 capitalize">{campaign.type} Campaign</p>
          </div>
        </div>
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <MoreVertical className="w-4 h-4 text-gray-400" />
          </button>
          <AnimatePresence>
            {showMenu && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                className="absolute right-0 top-full mt-1 w-48 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-10"
              >
                <button
                  onClick={() => { onView(); setShowMenu(false); }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                  <Eye className="w-4 h-4" /> View Details
                </button>
                <button
                  onClick={() => { onEdit(); setShowMenu(false); }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                  <Edit2 className="w-4 h-4" /> Edit Campaign
                </button>
                <button
                  onClick={() => { onDuplicate(); setShowMenu(false); }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                  <Copy className="w-4 h-4" /> Duplicate
                </button>
                {campaign.status === 'active' && (
                  <button
                    onClick={() => { onPause(); setShowMenu(false); }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Pause className="w-4 h-4" /> Pause Campaign
                  </button>
                )}
                <div className="my-2 border-t border-gray-100" />
                <button
                  onClick={() => { onDelete(); setShowMenu(false); }}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Status & Tags */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${status.color}`}>
          {status.icon}
          {status.label}
        </span>
        {campaign.tags.map(tag => (
          <span key={tag} className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
            {tag}
          </span>
        ))}
      </div>

      {/* Metrics */}
      {campaign.status !== 'draft' && campaign.sent > 0 && (
        <div className="grid grid-cols-4 gap-3 mb-4 p-3 bg-gray-50 rounded-xl">
          <div className="text-center">
            <p className="text-lg font-bold text-gray-900">{campaign.sent.toLocaleString()}</p>
            <p className="text-xs text-gray-500">Sent</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-gray-900">{campaign.opened.toLocaleString()}</p>
            <p className="text-xs text-gray-500">Opened</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-emerald-600">{campaign.openRate}%</p>
            <p className="text-xs text-gray-500">Open Rate</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-blue-600">{campaign.clickRate}%</p>
            <p className="text-xs text-gray-500">CTR</p>
          </div>
        </div>
      )}

      {/* Progress Bar for Active Campaigns */}
      {campaign.status === 'active' && campaign.recipients > 0 && (
        <div className="mb-4">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Delivery Progress</span>
            <span>{Math.round((campaign.sent / campaign.recipients) * 100)}%</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${(campaign.sent / campaign.recipients) * 100}%` }}
              className="h-full bg-gradient-to-r from-violet-500 to-purple-600 rounded-full"
            />
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
        <div className="flex items-center gap-4 text-sm text-gray-500">
          <span className="flex items-center gap-1">
            <Users className="w-4 h-4" />
            {campaign.recipients.toLocaleString()} recipients
          </span>
          {campaign.scheduledFor && (
            <span className="flex items-center gap-1 text-violet-600">
              <Clock className="w-4 h-4" />
              Scheduled: {new Date(campaign.scheduledFor).toLocaleDateString()}
            </span>
          )}
        </div>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onView}
          className="flex items-center gap-1.5 text-sm font-medium text-violet-600 hover:text-violet-700"
        >
          View Details
          <ArrowRight className="w-4 h-4" />
        </motion.button>
      </div>
    </motion.div>
  );
};

const PremiumCampaignsPage: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [totalCampaigns, setTotalCampaigns] = useState(0);

  // Form state for creating new campaign
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    type: 'email',
    subject: '',
    audience: 'all',
  });

  const [stats, setStats] = useState([
    { title: 'Active Campaigns', value: 0, change: 0, icon: <Play className="w-5 h-5" />, gradient: 'bg-gradient-to-br from-emerald-500 to-teal-600' },
    { title: 'Total Sent', value: '0', change: 0, icon: <Send className="w-5 h-5" />, gradient: 'bg-gradient-to-br from-blue-500 to-cyan-600' },
    { title: 'Avg Open Rate', value: '0%', change: 0, icon: <Eye className="w-5 h-5" />, gradient: 'bg-gradient-to-br from-violet-500 to-purple-600', subtitle: 'Industry: 21%' },
    { title: 'Avg Click Rate', value: '0%', change: 0, icon: <MousePointerClick className="w-5 h-5" />, gradient: 'bg-gradient-to-br from-amber-500 to-orange-600', subtitle: 'Industry: 2.5%' },
  ]);

  // Fetch campaigns from API
  const fetchCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await campaignsService.list({
        search: searchQuery || undefined,
        status: statusFilter?.toUpperCase() || undefined,
        type: typeFilter?.toUpperCase() || undefined,
      });
      
      if (response && response.campaigns) {
        setCampaigns(response.campaigns.map(transformCampaign));
        setTotalCampaigns(response.total || 0);
      }
    } catch (err) {
      console.error('Error fetching campaigns:', err);
      setError('Failed to load campaigns. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, statusFilter, typeFilter]);

  // Fetch stats from API
  const fetchStats = useCallback(async () => {
    try {
      const statsData = await campaignsService.getStats();
      if (statsData) {
        setStats([
          { title: 'Active Campaigns', value: statsData.byStatus?.ACTIVE || 0, change: 8, icon: <Play className="w-5 h-5" />, gradient: 'bg-gradient-to-br from-emerald-500 to-teal-600' },
          { title: 'Total Campaigns', value: statsData.total?.toLocaleString() || '0', change: 23, icon: <Send className="w-5 h-5" />, gradient: 'bg-gradient-to-br from-blue-500 to-cyan-600' },
          { title: 'Avg Open Rate', value: `${statsData.avgOpenRate || 0}%`, change: 5, icon: <Eye className="w-5 h-5" />, gradient: 'bg-gradient-to-br from-violet-500 to-purple-600', subtitle: 'Industry: 21%' },
          { title: 'Avg Click Rate', value: `${statsData.avgClickRate || 0}%`, change: 12, icon: <MousePointerClick className="w-5 h-5" />, gradient: 'bg-gradient-to-br from-amber-500 to-orange-600', subtitle: 'Industry: 2.5%' },
        ]);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Handle creating a new campaign
  const handleCreateCampaign = async () => {
    try {
      if (!newCampaign.name) {
        alert('Campaign name is required');
        return;
      }
      
      if (editingCampaign) {
        // Update existing campaign
        await campaignsService.update(editingCampaign.id, {
          name: newCampaign.name,
          type: newCampaign.type.toUpperCase(),
        });
        setEditingCampaign(null);
      } else {
        // Create new campaign
        await campaignsService.create({
          name: newCampaign.name,
          type: newCampaign.type.toUpperCase(),
        });
      }
      setShowCreateModal(false);
      setNewCampaign({ name: '', type: 'email', subject: '', audience: 'all' });
      fetchCampaigns();
      fetchStats();
    } catch (err) {
      console.error('Error saving campaign:', err);
      alert('Failed to save campaign');
    }
  };

  // Handle edit campaign - open modal with campaign data
  const handleEditCampaign = (campaign: Campaign) => {
    setEditingCampaign(campaign);
    setNewCampaign({
      name: campaign.name,
      type: campaign.type.toLowerCase(),
      subject: '',
      audience: 'all',
    });
    setShowCreateModal(true);
  };

  // Handle pause campaign
  const handlePauseCampaign = async (id: string) => {
    try {
      await campaignsService.updateStatus(id, 'PAUSED');
      fetchCampaigns();
    } catch (err) {
      console.error('Error pausing campaign:', err);
    }
  };

  // Handle duplicate campaign  
  const handleDuplicateCampaign = async (id: string) => {
    try {
      await campaignsService.duplicate(id);
      fetchCampaigns();
    } catch (err) {
      console.error('Error duplicating campaign:', err);
    }
  };

  // Handle view campaign details
  const handleViewCampaign = (campaign: Campaign) => {
    alert(`View campaign details: ${campaign.name}\n\nFull campaign editor coming soon!`);
  };

  // Handle delete campaign
  const handleDeleteCampaign = async (id: string) => {
    if (!confirm('Delete this campaign?')) return;
    try {
      await campaignsService.delete(id);
      fetchCampaigns();
      fetchStats();
    } catch (err) {
      console.error('Error deleting campaign:', err);
    }
  };

  // Server already filters via fetchCampaigns; keep local alias for the list UI
  const filteredCampaigns = campaigns;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white shadow-lg shadow-amber-500/25">
              <Megaphone className="w-6 h-6" />
            </div>
            Campaigns
          </h1>
          <p className="text-gray-600 mt-2">
            Create, manage and track your marketing campaigns
          </p>
        </div>

        <div className="flex items-center gap-3">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => window.location.href = '/analytics'}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition-colors"
          >
            <BarChart3 className="w-4 h-4" />
            Analytics
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 text-white font-medium shadow-lg shadow-violet-500/25"
          >
            <Plus className="w-4 h-4" />
            Create Campaign
          </motion.button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <StatCard {...stat} />
          </motion.div>
        ))}
      </div>

      {/* Search & Filters */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search campaigns..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none"
            />
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Status:</span>
            <div className="flex items-center bg-gray-100 rounded-xl p-1">
              {['all', 'active', 'paused', 'draft', 'completed', 'scheduled'].map(status => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status === 'all' ? null : status)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors capitalize ${
                    (status === 'all' && !statusFilter) || status === statusFilter
                      ? 'bg-white shadow text-violet-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>

          {/* Type Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Type:</span>
            <select
              value={typeFilter || ''}
              onChange={(e) => setTypeFilter(e.target.value || null)}
              className="px-3 py-2 rounded-xl border border-gray-200 text-sm focus:border-violet-500 outline-none"
            >
              <option value="">All Types</option>
              <option value="email">Email</option>
              <option value="sms">SMS</option>
              <option value="social">Social</option>
              <option value="automation">Automation</option>
            </select>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-16">
          <div className="w-12 h-12 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Loading campaigns...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="text-center py-16 bg-red-50 rounded-2xl border border-red-200">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchCampaigns}
            className="px-4 py-2 rounded-xl bg-red-100 text-red-700 font-medium hover:bg-red-200 transition-colors"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Campaigns Grid */}
      {!loading && !error && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredCampaigns.map((campaign, index) => (
            <motion.div
              key={campaign.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <CampaignCard
                campaign={campaign}
                onEdit={() => handleEditCampaign(campaign)}
                onPause={() => handlePauseCampaign(campaign.id)}
                onDuplicate={() => handleDuplicateCampaign(campaign.id)}
                onDelete={() => handleDeleteCampaign(campaign.id)}
                onView={() => handleViewCampaign(campaign)}
              />
            </motion.div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && filteredCampaigns.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="min-h-[50vh] flex items-center justify-center"
        >
          <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
            <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <Megaphone className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No campaigns found</h3>
            <p className="text-gray-500 mb-6">
              {totalCampaigns === 0 ? 'Create your first campaign to get started' : 'Try adjusting your filters or create a new campaign'}
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 text-white font-medium shadow-lg shadow-violet-500/25"
            >
              Create Your First Campaign
            </button>
          </div>
        </motion.div>
      )}

      {/* Create Campaign Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => { setShowCreateModal(false); setEditingCampaign(null); setNewCampaign({ name: '', type: 'email', subject: '', audience: 'all' }); }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl bg-white rounded-2xl shadow-2xl z-50 overflow-hidden"
            >
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-900">{editingCampaign ? 'Edit Campaign' : 'Create New Campaign'}</h2>
                  <button
                    onClick={() => { setShowCreateModal(false); setEditingCampaign(null); setNewCampaign({ name: '', type: 'email', subject: '', audience: 'all' }); }}
                    className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
              </div>

              <div className="p-6">
                {/* Campaign Type Selection */}
                <div className="mb-6">
                  <label className="text-sm font-medium text-gray-700 mb-3 block">Campaign Type</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { type: 'email', icon: <Mail className="w-5 h-5" />, label: 'Email', gradient: 'from-blue-500 to-cyan-500' },
                      { type: 'sms', icon: <MessageSquare className="w-5 h-5" />, label: 'SMS', gradient: 'from-emerald-500 to-teal-500' },
                      { type: 'social', icon: <Share2 className="w-5 h-5" />, label: 'Social', gradient: 'from-violet-500 to-purple-500' },
                      { type: 'automation', icon: <Zap className="w-5 h-5" />, label: 'Automation', gradient: 'from-amber-500 to-orange-500' },
                    ].map(item => (
                      <button
                        key={item.type}
                        type="button"
                        onClick={() => setNewCampaign(prev => ({ ...prev, type: item.type }))}
                        className={`p-4 rounded-xl border-2 transition-colors group ${
                          newCampaign.type === item.type
                            ? 'border-violet-500 bg-violet-50'
                            : 'border-gray-200 hover:border-violet-300'
                        }`}
                      >
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${item.gradient} flex items-center justify-center text-white shadow-lg mx-auto mb-2`}>
                          {item.icon}
                        </div>
                        <p className={`text-sm font-medium ${newCampaign.type === item.type ? 'text-violet-600' : 'text-gray-700 group-hover:text-violet-600'}`}>
                          {item.label}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Campaign Details */}
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Campaign Name *</label>
                    <input
                      type="text"
                      value={newCampaign.name}
                      onChange={(e) => setNewCampaign(prev => ({ ...prev, name: e.target.value }))}
                      className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none"
                      placeholder="e.g., Summer Travel Deals"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Subject Line</label>
                    <div className="relative mt-1">
                      <input
                        type="text"
                        value={newCampaign.subject}
                        onChange={(e) => setNewCampaign(prev => ({ ...prev, subject: e.target.value }))}
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none pr-24"
                        placeholder="Enter subject line..."
                      />
                      <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 px-3 py-1.5 rounded-lg bg-violet-100 text-violet-700 text-xs font-medium hover:bg-violet-200 transition-colors disabled:opacity-50" onClick={handleAIGenerate} disabled={generatingAI}>
                        <Sparkles className="w-3 h-3" />
                        {generatingAI ? 'Generating...' : 'AI Generate'}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Target Audience</label>
                    <select
                      value={newCampaign.audience}
                      onChange={(e) => setNewCampaign(prev => ({ ...prev, audience: e.target.value }))}
                      className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-violet-500 outline-none"
                    >
                      <option value="all">All Contacts</option>
                      <option value="vip">VIP Customers</option>
                      <option value="subscribers">Newsletter Subscribers</option>
                      <option value="leads">Leads Only</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => { setShowCreateModal(false); setEditingCampaign(null); setNewCampaign({ name: '', type: 'email', subject: '', audience: 'all' }); }}
                  className="px-4 py-2.5 rounded-xl text-gray-700 font-medium hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <div className="flex items-center gap-3">
                  {!editingCampaign && (
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          if (!newCampaign.name) { alert('Campaign name is required'); return; }
                          await campaignsService.create({ name: newCampaign.name, type: newCampaign.type.toUpperCase(), status: 'DRAFT' });
                          setShowCreateModal(false);
                          setNewCampaign({ name: '', type: 'email', subject: '', audience: 'all' });
                          fetchCampaigns(); fetchStats();
                        } catch { alert('Failed to save draft'); }
                      }}
                      className="px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-100 transition-colors"
                    >
                      Save as Draft
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleCreateCampaign}
                    className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 text-white font-medium shadow-lg shadow-violet-500/25 hover:opacity-90 transition-opacity"
                  >
                    {editingCampaign ? 'Save Changes' : 'Create Campaign'}
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PremiumCampaignsPage;
